/**
 * useMediaQuery (Bloco TBL.4).
 *
 * Hook minimalista, SSR-safe, sem dependencias. Re-renderiza quando o
 * matchMedia mudar de estado (resize, orientacao, etc).
 *
 * Uso:
 *   const isMobile = useMediaQuery("(max-width: 920px)");
 *   const isSmall = useMediaQuery("(max-width: 720px)");
 */

import { useEffect, useState } from "react";

function getInitialMatch(query) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

export default function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => getInitialMatch(query));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    let mql;
    try {
      mql = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const handler = (event) => setMatches(event.matches);
    // matchMedia.addEventListener("change", ...) eh moderno; Safari < 14
    // usa addListener legado. Tentar moderno, fallback no antigo.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
    } else if (typeof mql.addListener === "function") {
      mql.addListener(handler);
    }
    // Sync no mount caso o valor inicial nao tenha sido capturado
    // (StrictMode dupla execucao etc).
    setMatches(mql.matches);
    return () => {
      if (typeof mql.removeEventListener === "function") {
        mql.removeEventListener("change", handler);
      } else if (typeof mql.removeListener === "function") {
        mql.removeListener(handler);
      }
    };
  }, [query]);

  return matches;
}
